import React from 'react';
import { useTranslation } from 'react-i18next';
import { FarmOverviewCycle, isoWeekSpan } from '../../models/farms-overview';
import { isCycleBelowExpected } from '../../models/piglet-registration';
import { AnchorRect, useAnchorRect } from '../../hooks/useAnchorRect';
import { HoverCard } from '../../elements/ui/hover-card';
import { pigletRegistrationColors as c } from '../PigletRegistration/colors';

const WIDTH = 220;
const HEIGHT = 40;
const MAX_BAR_WIDTH = 16;

/** The ramp step for one cycle week. A cycle longer than the ramp keeps its lightest step for the extra weeks. */
const rampStep = (ramp: string[], index: number) => ramp[Math.min(index, ramp.length - 1)];

/** Cycles listed in the hover card. Enough to read the recent shape without turning it into a table. */
const TOOLTIP_CYCLES = 5;

const num = (value: number) => value.toLocaleString();

/** "wk 37–38", or "wk 41" for a 1-week cycle. */
const useWeekRangeLabel = () => {
  const { t } = useTranslation();

  return (from: string, to: string) => {
    const span = isoWeekSpan(from, to);
    return span.first === span.last
      ? t('f-farms-overview-week-single', { week: span.first })
      : t('f-farms-overview-week-range', { from: span.first, to: span.last });
  };
};

/**
 * The numbers behind the bars. The sparkline is 150px of shape with no axis and no labels, so without this
 * the reader can see that a farm is down but never by how much - and the row's headline only quotes the
 * scored total, not the cycle that broke.
 */
const TrendTooltip = ({
  cycles,
  farmName,
  anchor
}: {
  cycles: FarmOverviewCycle[];
  farmName: string;
  anchor: AnchorRect;
}) => {
  const { t } = useTranslation();
  const weekRange = useWeekRangeLabel();
  const recent = cycles.slice(-TOOLTIP_CYCLES);

  return (
    <HoverCard anchor={anchor} className="min-w-56">
      <p className="mb-1.5 font-medium text-gray-700">
        {t('f-farms-overview-tooltip-title-cycles', { farm: farmName, count: cycles.length })}
      </p>

      <div className="grid gap-1">
        {recent.map((cycle) => (
          <div key={cycle.startDate} className="flex items-center justify-between gap-6">
            <span className="text-gray-500">{weekRange(cycle.startDate, cycle.endDate)}</span>
            <span className="font-medium tabular-nums text-gray-900">
              {cycle.isComplete
                ? t('f-farms-overview-tooltip-cycle-value', {
                    registered: num(cycle.registered),
                    expected: num(cycle.expected),
                    percent: Math.round(cycle.percentOfExpected)
                  })
                : t('f-farms-overview-tooltip-cycle-running', {
                    registered: num(cycle.registered),
                    expected: num(cycle.expected)
                  })}
            </span>
          </div>
        ))}
      </div>

      <p className="mt-2 border-t border-gray-100 pt-1.5 text-gray-500">
        {t('f-farms-overview-tooltip-footer-cycles', { count: recent.length })}
      </p>
    </HoverCard>
  );
};

export interface FarmTrendSparklineProps {
  cycles: FarmOverviewCycle[];
  farmName: string;
}

/**
 * One bar per production cycle - the piglets registered in it, stacked by cycle week - against the farm's
 * expected line, in the width of a table cell.
 *
 * Red is spent on completed cycles under 80%, the same rule as the farm dashboard's chart. The cycle in
 * progress is hatched with a dotted outline up to the expected line: its count so far is not a verdict, and a
 * solid short bar would read as a collapse every time a new cycle starts.
 *
 * Hand-drawn SVG rather than a recharts chart: one of these renders per queue row, and recharts mounts a
 * responsive container and a resize observer per instance, which is a lot of machinery for a dozen
 * rectangles.
 *
 * A cycle the farm registered nothing in comes back as `registered: 0` and is drawn as an empty column, not
 * skipped. That hole is the whole point of the row above it - it is what "stopped sending data" looks like,
 * and closing the gap up would hide it.
 */
export default function FarmTrendSparkline({ cycles, farmName }: FarmTrendSparklineProps) {
  const { t } = useTranslation();
  const hatchId = `farm-trend-hatch-${React.useId()}`;
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const { anchor, openByPointer, openByFocus, clear } = useAnchorRect(wrapperRef);

  if (cycles.length === 0) return null;

  // Drawn from the running cycle, which is never cut short, so the line is the full per-cycle volume the
  // row's text quotes. A cycle a schedule change cut short keeps its prorated figure in the tooltip.
  const expected = cycles[cycles.length - 1].expected;

  // The expected line is usually the tallest thing here, so it has to be inside the domain. The headroom
  // keeps a bar that overshoots expected from touching the top edge.
  const peak = Math.max(expected, ...cycles.map((cycle) => cycle.registered)) * 1.15 || 1;
  const heightOf = (value: number) => (value / peak) * HEIGHT;

  const barWidth = Math.min(MAX_BAR_WIDTH, WIDTH / cycles.length - 2);
  const step = cycles.length > 1 ? (WIDTH - barWidth) / (cycles.length - 1) : 0;
  const expectedY = HEIGHT - heightOf(expected);

  return (
    // Focus opens the card too: the sparkline is the only place the per-cycle numbers exist, so reaching
    // them cannot depend on having a pointer.
    <div
      ref={wrapperRef}
      className="shrink-0"
      onMouseEnter={openByPointer}
      onMouseLeave={clear}
      onFocus={openByFocus}
      onBlur={clear}
    >
      <svg
        width={WIDTH}
        height={HEIGHT}
        viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
        role="img"
        tabIndex={0}
        aria-label={t('f-farms-overview-sparkline-label-cycles', { farm: farmName, count: cycles.length })}
        className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
      >
        <defs>
          <pattern id={hatchId} width={4} height={4} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
            <rect width={4} height={4} fill={c.inProgress.ground} />
            <line x1={0} y1={0} x2={0} y2={4} stroke={c.inProgress.stripe} strokeWidth={2} />
          </pattern>
        </defs>

        <line
          x1={0}
          y1={expectedY}
          x2={WIDTH}
          y2={expectedY}
          stroke={c.expected}
          strokeWidth={1.5}
          strokeDasharray="3 3"
        />

        {cycles.map((cycle, index) => {
          const x = index * step;

          if (!cycle.isComplete) {
            const height = heightOf(cycle.registered);
            const target = heightOf(Math.max(cycle.expected, cycle.registered));

            return (
              <g key={cycle.startDate}>
                <rect
                  x={x + 0.5}
                  y={HEIGHT - target + 0.5}
                  width={barWidth - 1}
                  height={Math.max(0, target - 1)}
                  rx={2}
                  fill="none"
                  stroke={c.inProgress.stripe}
                  strokeWidth={1}
                  strokeDasharray="2 2"
                />
                {cycle.registered > 0 && (
                  <rect
                    x={x}
                    y={HEIGHT - Math.max(2, height)}
                    width={barWidth}
                    height={Math.max(2, height)}
                    rx={2}
                    fill={`url(#${hatchId})`}
                  />
                )}
              </g>
            );
          }

          if (cycle.registered <= 0) return null;

          const below = isCycleBelowExpected(cycle);

          if (cycle.weeks.length <= 1) {
            const height = Math.max(2, heightOf(cycle.registered));

            return (
              <rect
                key={cycle.startDate}
                x={x}
                y={HEIGHT - height}
                width={barWidth}
                height={height}
                rx={2}
                fill={below ? c.belowExpected : c.registered}
              />
            );
          }

          // Stacked by cycle week, bottom first, in the same ramp as the farm dashboard's chart - the split
          // is what shows a 200/800 cycle as one farrowing batch rather than two bad weeks.
          let base = HEIGHT;

          return (
            <g key={cycle.startDate}>
              {cycle.weeks.map((week, weekIndex) => {
                if (week.registered <= 0) return null;

                const height = heightOf(week.registered);
                base -= height;

                return (
                  <rect
                    key={week.weekStartDate}
                    x={x}
                    y={base}
                    width={barWidth}
                    height={height}
                    fill={rampStep(below ? c.belowExpectedRamp : c.cycleWeekRamp, weekIndex)}
                    stroke="#ffffff"
                    strokeWidth={0.75}
                  />
                );
              })}
            </g>
          );
        })}
      </svg>

      {anchor && <TrendTooltip cycles={cycles} farmName={farmName} anchor={anchor} />}
    </div>
  );
}
